// @react
import React, { ChangeEvent, ReactElement, useState } from 'react'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Typography from '@material-ui/core/Typography'
// @common
import DigitalOrders from './DigitalOrders'
// @components
import colors from '@bit/atd.web.colors'
import ContextMenu from '@bit/atd.web.context-menu'
import ContextMenuItem from '@bit/atd.web.context-menu-item'
import Grid from '@bit/atd.web.grid'
// @module components
import AllOrders from 'modules/profile/components/OrderHistoryTabs/AllOrders'
import SubscriptionOrders from 'modules/profile/components/OrderHistoryTabs/SubscriptionOrders'
import Search from 'modules/common/components/PublicUserList/Search'
import useDebounce from 'modules/common/hooks/useDebounce'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

export type MembershipContext = {
  title: string
  orderNumber: string
  orderLineNumber?: number
} | null

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {<div style={{ marginTop: '24px' }}>{children}</div>}
    </div>
  )
}

const a11yProps = (index: number) => ({
  id: `tab-${index}`,
  'aria-controls': `tabpanel-${index}`,
})

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    marginBottom: '16px',
    [theme.breakpoints.up('md')]: {
      marginBottom: '24px',
    },
  },
  titleCol: {
    display: 'flex',
  },
  title: {
    display: 'inline-block',
    margin: '0',
    marginRight: '10px',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '32px',
    color: colors.black,
    '& + button': {
      marginLeft: '16px',
    },
  },
  searchCol: {
    zIndex: 0,
    marginTop: '24px',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      marginTop: '0',
      width: 'auto',
    },
  },
  tabHeader: {
    marginBottom: '32px',
    borderBottom: '1px solid #DBD9D6',
    '& .MuiTab-root': {
      fontSize: '14px',
      lineHeight: '14px',
    },
    '& .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      paddingBottom: '8px',
    },
    '& .MuiTab-wrapper > span:first-child': {
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      fontWeight: '600',
    },
    '& .MuiTab-wrapper > span + span': {
      border: '1px solid #DBD9D6',
      borderRadius: '12px',
      color: '#424140',
      fontSize: '14px',
      fontWeight: '500',
      padding: '4px 8px',
      marginLeft: '8px',
    },
  },
}))

const OrderHistoryTabs = (): ReactElement => {
  /****************************************
   *  STATE
   ****************************************/

  const [value, setValue] = React.useState(0)
  const [timeLimit, setTimeLimit] = useState<number | undefined>(30)
  const [allOrderCount, setAllOrderCount] = useState<number>(0)
  const [subscriptionCount, setSubscriptionCount] = useState<number>(0)
  const [digitalCount, setDigitalCount] = useState<number>(0)
  const [searchKeyword, setSearchKeyword] = useState<string>('')

  /****************************************
   *  HOOKS
   ****************************************/

  const classes = useStyles()
  const debouncedSearchKeyword = useDebounce(searchKeyword) as string

  /****************************************
   *  FUNCTIONS
   ****************************************/

  const handleChange = (
    event: React.ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setValue(newValue)
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value)
  }

  /****************************************
   *  RENDER
   ****************************************/

  return (
    <>
      <div>
        <Grid
          className={classes.header}
          container
          justify="space-between"
          alignItems="center"
        >
          <Grid item className={classes.titleCol} xs={12} md={6}>
            <Typography variant="h2" className={classes.title}>
              Your Orders
            </Typography>
            <ContextMenu
              toggle={!timeLimit ? 'Time Limit' : `Last ${timeLimit} days`}
              size="small"
              menu={
                <>
                  <ContextMenuItem onClick={() => setTimeLimit(30)} small>
                    Last 30 days
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => setTimeLimit(60)} small>
                    Last 60 days
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => setTimeLimit(90)} small>
                    Last 90 days
                  </ContextMenuItem>
                  {timeLimit ? (
                    <ContextMenuItem
                      onClick={() => setTimeLimit(undefined)}
                      small
                    >
                      No Limit
                    </ContextMenuItem>
                  ) : null}
                </>
              }
            />
          </Grid>

          <Grid item className={classes.searchCol} xs={12} md={5}>
            <Search onChange={handleSearch} placeholder="Search Orders" />
          </Grid>
        </Grid>

        <div>
          <Tabs
            className={classes.tabHeader}
            value={value}
            onChange={handleChange}
            variant="scrollable"
            aria-label="Order History"
          >
            <Tab
              label={
                <>
                  <span>All Orders</span>
                  <span>{allOrderCount}</span>
                </>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <>
                  <span>Digital Orders</span>
                  <span>{digitalCount}</span>
                </>
              }
              {...a11yProps(1)}
            />
            <Tab
              label={
                <>
                  <span>Subscriptions</span>
                  <span>{subscriptionCount}</span>
                </>
              }
              {...a11yProps(1)}
            />
          </Tabs>

          <TabPanel value={value} index={0}>
            <AllOrders
              timeLimit={timeLimit}
              searchKeyword={debouncedSearchKeyword}
              setCount={setAllOrderCount}
            />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <DigitalOrders
              timeLimit={timeLimit}
              searchKeyword={debouncedSearchKeyword}
              setCount={setDigitalCount}
            />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <SubscriptionOrders
              timeLimit={timeLimit}
              searchKeyword={debouncedSearchKeyword}
              setCount={setSubscriptionCount}
            />
          </TabPanel>
        </div>
      </div>
    </>
  )
}

export default OrderHistoryTabs
